import {Component, effect, Input, OnChanges, OnDestroy, OnInit, signal, SimpleChanges, ViewChild} from '@angular/core';
import {MatSort, MatSortModule, Sort} from '@angular/material/sort';
import {MatTableDataSource, MatTableModule} from '@angular/material/table';
import {firstValueFrom, Observable, of, Subject} from 'rxjs';
import {CommonDirectives} from '@app/shared/directives/common-directives';
import {Appointment, NgAppointments} from '@app/shared/interfaces/appointment.interface';
import {DashboardType} from '@app/core/enums/dashboard-types.enum';

import {MatDialog} from "@angular/material/dialog";
import {appointmentType} from "@app/shared/components/generic-dashboard/generic-dashboard.component";
import {PaginationComponent} from "@app/shared/components/pagination/pagination.component";
import {PageSearchSortOptions} from "@app/shared/interfaces/pageSearchSortOptions.interface";
import {AppointmentSearchParams} from "@app/shared/interfaces/appointmentSearchParams.interface";

import {
  AppointmentDetailModalComponent
} from "@app/shared/components/appointment-detail-modal/appointment-detail-modal.component";
import {rxResource} from "@angular/core/rxjs-interop";
import {MatProgressSpinner} from "@angular/material/progress-spinner";
import { Overlay } from '@angular/cdk/overlay';
import {MatButtonModule} from '@angular/material/button';
import {MatMenuModule} from '@angular/material/menu';
import {ToastService} from '@app/core/services/toast.service';
import {
  AppointmentExportFormat,
  exportAppointmentResults
} from '@app/shared/components/appointment-list/appointment-export';

@Component({
  selector: 'app-appointment-list',
  imports: [
    MatTableModule,
    MatSortModule,
    CommonDirectives,
    PaginationComponent,
    MatProgressSpinner,
    MatButtonModule,
    MatMenuModule
  ],
  templateUrl: './appointment-list.component.html',
  styleUrl: './appointment-list.component.scss'
})
export class AppointmentListComponent implements OnChanges, OnDestroy, OnInit {
  protected readonly appointmentType = appointmentType;

  @ViewChild(MatSort) sort!: MatSort;
  @Input() title!: string;
  @Input() dashboardType?: DashboardType;
  @Input() showReferringProvider = false;
  @Input() showStatus = false;
  @Input() appointmentListType!: appointmentType;
  @Input() defaultSortField = 'appointmentDate';
  @Input() defaultSortDirection: 'asc'|'desc'  = 'asc';
  @Input() search = '';
  @Input({ required: true }) fetchAppointments$!: (filter: AppointmentSearchParams) => Observable<NgAppointments>;

  // Maximum number of days to look for upcoming/searchable appointments
  private readonly maxFutureDays: number = 180;

  // Number of months to look for previous appointments and searchable appointments
  private readonly minPrevMonth: number = 6;
  private readonly maxExportPages = 10;
  readonly defaultPageSize: number = 25;
  readonly isExporting = signal(false);
  totalPages: number = 0;

  $ngDestroy = new Subject();
  private readonly baseDisplayedColumns: string[] = ['date', 'name', 'dob', 'iowaOrthoProvider', 'location', 'visit' ];
  displayedColumns: string[] = [...this.baseDisplayedColumns];
  tableDataSource = new MatTableDataSource<Appointment>([]);

  emptyTableMessage: string = "No records found. Please adjust your filters or search criteria and try again.";


  resourceSortOptions = signal<PageSearchSortOptions>({
    pageNumber: 1,
    pageSize: this.defaultPageSize,
    searchTerm: '',
    sortField: this.defaultSortField,
    sortDirection: this.defaultSortDirection,
    secondarySortField: "lastName",
    secondarySortDirection: "asc"
  });

  resourceData = rxResource<NgAppointments, PageSearchSortOptions>({
    params: () => this.resourceSortOptions(),
    stream: (params) => {
      const searchTerm = (params.params.searchTerm ?? '').trim();
      if(this.appointmentListType == appointmentType.PatientSearch && searchTerm == '') {
        const searchData: NgAppointments = {
          items: [],
          count: 0
        }
        this.emptyTableMessage = 'Enter search criteria above to view results.';
        return of(searchData);
      }
      this.emptyTableMessage = "No records found. Please adjust your filters or search criteria and try again.";
      return this.fetchAppointments$(this.createAppointmentSearchParams(params.params));
    }
  });

  constructor(
    private dialog: MatDialog,
    private overlay: Overlay,
    private toastService: ToastService
  ) {
    effect(() => {
      const data = this.resourceData.value();
      if (data) {
        this.tableDataSource.data = data.items;
        this.totalPages = Math.ceil(data.count / this.defaultPageSize);
      } else {
        this.tableDataSource.data = [];
      }
    });
  }

  ngOnInit(): void {
    this.resourceSortOptions.update((current) => ({...current, sortDirection: this.defaultSortDirection}));
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['showReferringProvider'] || changes['showStatus']) {
      this.syncDisplayedColumns();
    }

    if(changes['search']) {
      this.resourceSortOptions.update((current) => ({...current, pageNumber: 1, searchTerm: (this.search ?? '').trim()}));
    }
  }

  ngOnDestroy(): void {
    this.$ngDestroy.next(true);
  }

  public onSortChange(sort: Sort){
    const direction = sort.direction;
    if (direction !== 'asc' && direction !== 'desc') {
      return;
    }

    let sortField = this.resourceSortOptions().sortField;
    let secondarySortField = this.resourceSortOptions().secondarySortField;

    switch(sort.active){
      case 'date':
        sortField = 'appointmentDate';
        secondarySortField = 'lastName';
        break;
      case 'name':
        sortField = 'lastName';
        secondarySortField = 'appointmentDate';
        break;
      case 'visit':
        sortField = 'eventName';
        secondarySortField = 'appointmentDate';
        break;
      case 'location':
        sortField = 'locationName';
        secondarySortField = 'appointmentDate';
        break;
      case 'iowaOrthoProvider':
        sortField = 'iowaOrthoProvider';
        secondarySortField = 'appointmentDate';
        break;
      case 'referringProvider':
        sortField = 'referringProvider';
        secondarySortField = 'appointmentDate';
        break;
      default:
        return;
    }

    this.resourceSortOptions.update((current) => ({
      ...current,
      pageNumber: 1,
      sortField,
      sortDirection: direction,
      secondarySortField,
      secondarySortDirection: 'asc'
    }));
  }

  onRowClicked(row: any) {
    this.openAppointmentInfoModal(row.id, row.patient, row.encounterId);
  }

  private syncDisplayedColumns(): void {
    if(this.showStatus){
        this.displayedColumns = [...this.baseDisplayedColumns, 'status'];
    }

    if(this.showReferringProvider && this.displayedColumns.length > 5){
      this.displayedColumns.splice(5, 0, 'referringProvider');
    }
  }
  getDisplayedColumns(): string[] {
    return this.displayedColumns;
  }

  onPageChange(newPage: number) {
    this.resourceSortOptions.update((current) => ({...current, pageNumber: newPage}));
  }

  async exportAppointments(format: AppointmentExportFormat): Promise<void> {
    if (this.isExporting() || !this.resourceData.value()?.count) {
      return;
    }

    this.isExporting.set(true);
    try {
      const appointments = await this.fetchAllAppointmentsForExport();
      if (appointments.length === 0) {
        this.toastService.showWarningToast('There are no appointments to export.');
        return;
      }

      await exportAppointmentResults(appointments, format, this.getExportOptions());
    } catch (error: unknown) {
      console.error('Unable to export appointments.', error);
      this.toastService.showErrorToast('Unable to export appointments. Please try again.');
    } finally {
      this.isExporting.set(false);
    }
  }

  private createAppointmentSearchParams(
    options: PageSearchSortOptions,
    pageNumber = options.pageNumber,
    pageSize = options.pageSize,
    referenceDate = new Date()
  ): AppointmentSearchParams {
    const currentDate = referenceDate.toISOString().slice(0, 19);
    const minDate = new Date(referenceDate);
    minDate.setMonth(minDate.getMonth() - this.minPrevMonth);
    const maxDate = new Date(referenceDate);
    maxDate.setDate(maxDate.getDate() + this.maxFutureDays);

    const dateRange = this.appointmentListType === appointmentType.FutureAppointments
      ? {minDate: currentDate, maxDate: maxDate.toISOString().slice(0, 19)}
      : this.appointmentListType === appointmentType.PastAppointments
        ? {minDate: minDate.toISOString().slice(0, 19), maxDate: currentDate}
        : {minDate: minDate.toISOString().slice(0, 19), maxDate: maxDate.toISOString().slice(0, 19)};

    return {
      ...dateRange,
      page: pageNumber.toString(),
      limit: pageSize.toString(),
      orderBy: `${options.sortField} ${options.sortDirection},${options.secondarySortField} ${options.secondarySortDirection}`,
      searchText: (options.searchTerm ?? '').trim(),
      listType: this.appointmentListType
    };
  }

  private async fetchAllAppointmentsForExport(): Promise<Appointment[]> {
    const options = this.resourceSortOptions();
    const referenceDate = new Date();
    const firstPage = await firstValueFrom(this.fetchAppointments$(
      this.createAppointmentSearchParams(options, 1, options.pageSize, referenceDate)
    ));
    const appointments = [...firstPage.items];
    const pageCount = Math.min(
      Math.ceil(firstPage.count / options.pageSize),
      this.maxExportPages
    );

    for (let pageNumber = 2; pageNumber <= pageCount; pageNumber++) {
      const page = await firstValueFrom(this.fetchAppointments$(
        this.createAppointmentSearchParams(options, pageNumber, options.pageSize, referenceDate)
      ));
      appointments.push(...page.items);
    }

    return appointments;
  }

  private getExportOptions() {
    return {
      title: this.title,
      showReferringProvider: this.showReferringProvider,
      showStatus: this.showStatus
    };
  }

  openAppointmentInfoModal(appointmentId: string | null, patient: any, encounterId: string | null) {
    if (!appointmentId) { return; }
    const isReferralAppointmentsModal = this.dashboardType === DashboardType.Referral;

    const dialogRef = this.dialog.open(AppointmentDetailModalComponent, {
      width: '700px',
      autoFocus: false,
      disableClose: false,
      scrollStrategy: this.overlay.scrollStrategies.block()
    });

    dialogRef.componentInstance.appointmentDetailParams = {
      patientName: `${patient.firstName} ${patient.lastName}`,
      dateOfBirth: patient.dateOfBirth,
      appointmentId: appointmentId,
      appointmentType: this.appointmentListType,
      ...(isReferralAppointmentsModal ? { showDocumentsTable: true } : {})
    }

    dialogRef.componentInstance.detailSignal.set({
      appointmentId: appointmentId,
      patientId: patient.id,
      caseNumber: '',
      appointmentType: this.appointmentListType
    });
  }
}
